/**
 * Screen specs + Inspect tokens/css for ZiniHub wizard wireframes.
 * Feeds <Inspect tokens css> in ScreenWF; dev brief via buildFlowContext.
 */
import { T } from "../../tokens";
import { resolveTokenExport, shellBlurbForName } from "../../tokenExportMap";

/** Flow step id → implemented ScreenWF key */
export const FLOW_SCREEN_ALIASES = {
  "biz-catalog": "item-catalog",
  "biz-variants": "product-form",
  "variants": "product-form",
};

export function resolveScreenId(screenOrStepId) {
  return FLOW_SCREEN_ALIASES[screenOrStepId] || screenOrStepId;
}

export const SCREEN_CATALOG = {
  "admin-login": {
    label: "Admin Login",
    route: "/login",
    shell: "PublicShell",
    auth: "public",
    app: "admin",
    primaryAction: "LOGIN",
    states: ["default", "invalid-phone", "country-code-mismatch", "wrong-password", "locked", "success"],
    mobile: "Split brand panel hidden; centred card with logo. Inline errors below fields.",
    tierGating: "None",
    edgeCases: ["Password-based auth — no OTP", "Invalid credentials → inline error + Alert", "Lockout after N attempts with timer", "Redirect to /dashboard on success", "Forgot password link"],
  },
  "cust-login": {
    label: "Customer Login",
    route: "/login",
    shell: "CustShell-minimal",
    auth: "public",
    app: "customer",
    primaryAction: "LOGIN",
    states: ["default", "invalid-phone", "country-code-mismatch", "wrong-password", "locked", "success"],
    mobile: "Centred card; country code + mobile row stacks if narrow. Inline validation errors.",
    tierGating: "None",
    edgeCases: ["Password-based auth — no OTP", "Post-login redirect to prior page or /orders", "Lockout timer on max attempts"],
  },
  "admin-register": {
    label: "Register — Step 1",
    route: "/register",
    shell: "PublicShell",
    auth: "public",
    app: "both",
    primaryAction: "Continue →",
    states: ["step1", "otp-visible"],
    mobile: "Progress bar + OTP inline boxes.",
    tierGating: "None",
    edgeCases: ["Verify enables Continue", "Resend OTP countdown"],
  },
  "register-s2": {
    label: "Register — Step 2",
    route: "/register",
    shell: "PublicShell",
    auth: "public",
    app: "both",
    primaryAction: "Continue →",
    states: ["step2"],
    mobile: "Name fields stack on mobile.",
    tierGating: "None",
    edgeCases: ["Password match validation"],
  },
  "register-s3": {
    label: "Register — Step 3",
    route: "/register",
    shell: "PublicShell",
    auth: "public",
    app: "both",
    primaryAction: "Register →",
    states: ["step3-optional"],
    mobile: "Optional fields skippable.",
    tierGating: "None",
    edgeCases: ["Auto-username read-only", "Email verify optional"],
  },
  "admin-reset": {
    label: "Password Reset — Step 1",
    route: "/resetpassword",
    shell: "PublicShell",
    auth: "public",
    app: "both",
    primaryAction: "Continue →",
    states: ["step1-verify", "otp-visible", "otp-cooldown", "wrong-otp", "otp-expired", "max-attempts"],
    mobile: "Same split layout as login. 2-step progress bar.",
    tierGating: "None",
    edgeCases: ["Step 1: mobile + OTP verify", "Resend countdown", "Wrong OTP inline error"],
  },
  "admin-reset-s2": {
    label: "Password Reset — Step 2",
    route: "/resetpassword",
    shell: "PublicShell",
    auth: "public",
    app: "both",
    primaryAction: "Reset Password",
    states: ["step2-change", "success", "failure"],
    mobile: "Same split layout as login. Progress bar step 2.",
    tierGating: "None",
    edgeCases: ["Password match validation", "Redirect to login on success", "Server error on failure"],
  },
  "cust-reset": {
    label: "Password Reset — Step 1",
    route: "/resetpassword",
    shell: "CustShell-minimal",
    auth: "public",
    app: "customer",
    primaryAction: "Continue →",
    states: ["step1-verify", "otp-visible", "otp-cooldown", "wrong-otp", "otp-expired", "max-attempts"],
    mobile: "50-50 split layout. 2-step progress bar.",
    tierGating: "None",
    edgeCases: ["Step 1: mobile + OTP verify", "Resend countdown"],
  },
  "cust-reset-s2": {
    label: "Password Reset — Step 2",
    route: "/resetpassword",
    shell: "CustShell-minimal",
    auth: "public",
    app: "customer",
    primaryAction: "Reset Password",
    states: ["step2-change", "success", "failure"],
    mobile: "50-50 split layout. Progress bar step 2.",
    tierGating: "None",
    edgeCases: ["Password match validation", "Redirect on success", "Server error on failure"],
  },
  "hub-setup": {
    label: "Hub Setup Modal",
    route: "/hub",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Confirm & Submit",
    states: ["first-run-modal"],
    mobile: "Modal full-width on mobile.",
    tierGating: "Trial+",
    edgeCases: ["Domain availability check", "visitmyhub.online preview URL"],
  },
  "biz-grid": {
    label: "My Hub — Biz Grid",
    route: "/hub",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "+ Add New Biz",
    states: ["live-tab", "draft-tab", "empty-trash"],
    mobile: "Single-column card grid.",
    tierGating: "Store count per plan",
    edgeCases: ["Private vs public badges", "Edit navigates to /biz/edit/:bizId"],
  },
  "biz-settings": {
    label: "Biz Settings",
    route: "/biz/edit/:bizId",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Save settings",
    states: ["loaded", "gateway-locked-pre-kyc"],
    mobile: "Segmented toggles stack.",
    tierGating: "Gateway payment requires KYC (Starter+)",
    edgeCases: ["Delivery options", "Payment method", "Return policy fields"],
  },
  "hub-settings-main": {
    label: "Hub Settings — Overview",
    route: "/hub-settings",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Start Verification",
    states: ["kyc-required-banner", "kyc-verified"],
    mobile: "KYC card stacks above settings list.",
    tierGating: "KYC from Starter+",
    edgeCases: ["Link to /hub-settings/kyc"],
  },
  "kyc-pending": {
    label: "KYC — Not Started",
    route: "/hub-settings/kyc",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Start KYC",
    states: ["not-started"],
    mobile: "Two-column unlock/requirements stack.",
    tierGating: "Starter+",
    edgeCases: ["Opened from locked gateway toggle"],
  },
  "kyc-upload": {
    label: "KYC — Upload Documents",
    route: "/hub-settings/kyc",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Submit for Review",
    states: ["upload-step-1", "upload-step-2", "upload-step-3"],
    mobile: "Steps as vertical accordion.",
    tierGating: "Starter+",
    edgeCases: ["Identity front+back", "Bank cheque upload"],
  },
  "kyc-requested": {
    label: "KYC — Under Review",
    route: "/hub-settings/kyc",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "View submitted docs",
    states: ["under-review"],
    mobile: "Read-only doc thumbnails scroll horizontally.",
    tierGating: "Starter+",
    edgeCases: ["Cannot resubmit while pending", "WhatsApp notification on change"],
  },
  "kyc-verified": {
    label: "KYC — Verified",
    route: "/hub-settings/kyc",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Re-verify",
    states: ["verified"],
    mobile: "Green status chip prominent.",
    tierGating: "Starter+",
    edgeCases: ["Gateway unlocked in Biz Settings", "Storefront trust badge"],
  },
  "kyc-rejected": {
    label: "KYC — Rejected",
    route: "/hub-settings/kyc",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Re-upload Documents",
    states: ["rejected"],
    mobile: "Rejection reason card full-width.",
    tierGating: "Starter+",
    edgeCases: ["Flagged sections highlighted on re-upload"],
  },
  "order-monitoring": {
    label: "Order Dashboard",
    route: "/dashboard",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Export",
    states: ["loaded", "bulk-select", "empty"],
    mobile: "Table becomes card-per-order below 768px.",
    tierGating: "Orders from Starter+",
    edgeCases: ["Public vs private order tabs", "Bulk confirm/cancel bar"],
  },
  "sub-overview": {
    label: "Subscription — Plan Overview",
    route: "/subscription",
    shell: "AppShell",
    auth: "protected",
    app: "admin",
    primaryAction: "Renew",
    states: ["loaded"],
    mobile: "Plan stat cards 2×2 grid.",
    tierGating: "Hub-scoped via hub switcher",
    edgeCases: ["Upgrade 7-day window gate", "Cancel inline popover"],
  },
  "discover-page": {
    label: "Discover Page",
    route: "/discover",
    shell: "CustShell",
    auth: "public",
    app: "customer",
    primaryAction: "Search",
    states: ["idle", "results", "error-503", "empty-results"],
    mobile: "Filters collapse; layout toggle hidden on narrow viewports.",
    tierGating: "None",
    edgeCases: ["503 must not use empty-results state", "Issue #2 — error card + Retry"],
  },
  "hub-storefront": {
    label: "Hub Storefront",
    route: "/hub",
    shell: "CustShell",
    auth: "public",
    app: "customer",
    primaryAction: "Open store card",
    states: ["loaded", "private-card-visible"],
    mobile: "Store cards single column.",
    tierGating: "None",
    edgeCases: ["Only Live+Public in grid", "Private card shows lock not blur"],
  },
  "biz-page": {
    label: "Biz Page — Products",
    route: "/:bizSlug",
    shell: "CustShell",
    auth: "public",
    app: "customer",
    primaryAction: "Add to Cart",
    states: ["products-tab", "services-tab"],
    mobile: "Product cards stack; variant radios wrap.",
    tierGating: "None",
    edgeCases: ["Indicator row: open, delivery, payment, returns", "Strip bizId from display names"],
  },
  "biz-private": {
    label: "Private Store — Passkey",
    route: "/:bizSlug",
    shell: "CustShell",
    auth: "public",
    app: "customer",
    primaryAction: "Unlock",
    states: ["locked-catalog", "wrong-passkey"],
    mobile: "Passkey modal nearly full-screen.",
    tierGating: "None",
    edgeCases: ["Session-scoped unlock", "Shake animation on error"],
  },
  "cart-filled": {
    label: "Cart Drawer — With Items",
    route: "drawer",
    shell: "CustShell",
    auth: "public|protected",
    app: "customer",
    primaryAction: "Checkout",
    states: ["filled", "login-required"],
    mobile: "Drawer overlays content; full height.",
    tierGating: "None",
    edgeCases: ["Checkout triggers login if guest", "Items grouped by biz"],
  },
  "checkout-page": {
    label: "Checkout",
    route: "/checkout",
    shell: "CustShell",
    auth: "protected",
    app: "customer",
    primaryAction: "Place order",
    states: ["loaded", "address-picker"],
    mobile: "Accordions stack; place-order CTA sticky bottom.",
    tierGating: "None",
    edgeCases: ["Delivery address save incomplete — known bug", "Order summary grouped by biz"],
  },
  "orders-unified": {
    label: "Unified Order Monitoring",
    route: "/orders",
    shell: "CustShell",
    auth: "protected",
    app: "customer",
    primaryAction: "Export CSV",
    states: ["loaded", "sign-in-banner"],
    mobile: "Progress stepper compresses labels.",
    tierGating: "None",
    edgeCases: ["3 stat cards", "Per-order 5-step stepper"],
  },
  "profile-view": {
    label: "Profile View",
    route: "/profile",
    shell: "CustShell",
    auth: "protected",
    app: "customer",
    primaryAction: "Edit profile",
    states: ["view", "no-addresses"],
    mobile: "Personal details and addresses stack.",
    tierGating: "None",
    edgeCases: ["DOB optional display", "Address nicknames"],
  },
};

function tokenEntry(tokenRef, dark, resolvedOverride) {
  const resolved = resolveTokenExport(tokenRef, dark);
  return {
    token: resolved.token,
    cssVar: resolved.cssVar,
    theme: resolved.theme,
    resolved: resolvedOverride ?? resolved.resolved,
  };
}

function shellSurfaceTokens(shell, dark) {
  const isPublic = shell.includes("Public");
  const isCust = shell.includes("Cust");
  if (isPublic) {
    return {
      tokens: {
        background: tokenEntry("T.brand", dark, `linear-gradient(135deg, ${T.brand} 0%, ${T.violet} 100%)`),
        surface: tokenEntry("th.surface", dark),
        color: tokenEntry("th.text", dark),
        padding: tokenEntry("T.rmd", dark, dark ? "16px 20px" : "16px 20px"),
        borderRadius: tokenEntry("T.rlg", dark, T.rlg),
      },
      css: {
        background: `linear-gradient(135deg, ${T.brand} 0%, ${T.violet} 100%)`,
        surface: dark ? T.dSurface : T.lSurface,
        color: dark ? T.dText : T.lText,
        padding: "16px 20px",
        borderRadius: T.rlg,
      },
    };
  }
  if (isCust) {
    return {
      tokens: {
        background: tokenEntry("th.bg", dark),
        surface: tokenEntry("th.surface", dark),
        sidebar: tokenEntry("th.sidebar", dark),
        color: tokenEntry("th.text", dark),
        padding: tokenEntry("T.rmd", dark, "12px 14px"),
      },
      css: {
        background: dark ? T.dCanvas : T.lCanvas,
        surface: dark ? T.dSurface : T.lSurface,
        sidebar: dark ? "#0d1017" : T.lSurface,
        color: dark ? T.dText : T.lText,
        padding: "12px 14px",
      },
    };
  }
  return {
    tokens: {
      background: tokenEntry("th.bg", dark),
      surface: tokenEntry("th.surface", dark),
      sidebar: tokenEntry("th.sidebar", dark),
      color: tokenEntry("th.text", dark),
      padding: tokenEntry("T.rmd", dark, "12px"),
    },
    css: {
      background: dark ? T.dCanvas : T.lCanvas,
      surface: dark ? T.dSurface : T.lSurface,
      sidebar: dark ? "#0d1017" : T.lSurface,
      color: dark ? T.dText : T.lText,
      padding: "12px",
    },
  };
}

export function buildDevBrief(screenId) {
  const resolvedId = resolveScreenId(screenId);
  const catalog = SCREEN_CATALOG[resolvedId] || SCREEN_CATALOG[screenId];
  if (!catalog) return null;
  return {
    route: catalog.route,
    shell: catalog.shell,
    auth: catalog.auth,
    app: catalog.app,
    primaryAction: catalog.primaryAction,
    states: catalog.states,
    mobile: catalog.mobile,
    tierGating: catalog.tierGating,
    edgeCases: catalog.edgeCases,
  };
}

/** @param {{ flow: object, step: object, screenId?: string }} params */
export function buildFlowContext({ flow, step, screenId }) {
  if (!flow || !step) return null;
  const resolvedId = resolveScreenId(screenId || step.id);
  const catalog = SCREEN_CATALOG[resolvedId];
  const shell = step.shell || catalog?.shell || "AppShell";
  return {
    flowLabel: flow.label,
    stepLabel: step.label,
    stepNote: step.note || "",
    shell,
    shellBlurb: shellBlurbForName(shell),
    devBrief: catalog
      ? buildDevBrief(resolvedId)
      : {
          route: "—",
          shell,
          auth: "—",
          primaryAction: "—",
          states: ["stub"],
          edgeCases: [step.note || "Wireframe stub — not yet in ScreenWF."],
        },
  };
}

/**
 * @param {string} screenId
 * @param {{ dark: boolean, flowNote?: string }} options
 */
export function getScreenInspectMeta(screenId, { dark }) {
  const resolvedId = resolveScreenId(screenId);
  const catalog = SCREEN_CATALOG[resolvedId] || SCREEN_CATALOG[screenId];
  const label = catalog?.label || screenId;
  const shell = catalog?.shell || "AppShell";
  const { tokens, css } = shellSurfaceTokens(shell, dark);

  return {
    inspectId: `${resolvedId}-screen`,
    label,
    tokens,
    css,
  };
}

export function getFlowStepNote(flows, flowId, stepId) {
  const flow = flows.find((entry) => entry.id === flowId);
  const step = flow?.steps?.find((entry) => entry.id === stepId);
  return step?.note || "";
}

/**
 * Screen Layout Schemas (Phase B/C)
 *
 * Machine-readable component tree for each screen.
 * Consumed by /doit agents via bundle.design.screenLayouts.
 */
export const SCREEN_LAYOUTS = {
  "admin-login": {
    shell: "PublicAuthShell",
    shellProps: {},
    regions: [
      {
        type: "section",
        id: "form-area",
        direction: "column",
        align: "center",
        justify: "center",
        children: [
          {
            type: "card",
            id: "login-card",
            padding: 32,
            width: 400,
            children: [
              { type: "text", id: "login-title", text: "Login to ZiniHub", variant: "pageTitle", align: "center" },
              {
                type: "form",
                id: "login-form",
                fields: [
                  { id: "mobile", type: "phone", label: "Mobile Number", required: true, countryCode: true },
                  { id: "password", type: "password", label: "Password", required: true },
                ],
                submit: { label: "LOGIN", variant: "primary", width: "100%" },
                linkAfterSubmit: { label: "Forgot Password?", action: "/resetpassword" },
              },
            ],
          },
        ],
      },
    ],
  },

  "cust-login": {
    shell: "CustAuthShell",
    shellProps: {},
    regions: [
      {
        type: "section",
        id: "form-area",
        direction: "column",
        align: "center",
        justify: "center",
        children: [
          {
            type: "card",
            id: "login-card",
            padding: 28,
            width: 380,
            children: [
              { type: "text", id: "login-title", text: "Sign In", variant: "pageTitle", align: "center" },
              {
                type: "form",
                id: "login-form",
                fields: [
                  { id: "mobile", type: "phone", label: "Mobile Number", required: true, countryCode: true },
                  { id: "password", type: "password", label: "Password", required: true },
                ],
                submit: { label: "SIGN IN", variant: "primary", width: "100%" },
                linkAfterSubmit: { label: "Forgot Password?", action: "/resetpassword" },
              },
            ],
          },
        ],
      },
    ],
  },

  "hub-setup": {
    shell: "AdminAppShell",
    shellProps: { active: "My Hub", pageTitle: "My Hub" },
    regions: [
      {
        type: "section",
        id: "hub-content",
        direction: "column",
        padding: [14, 16],
        gap: 16,
        children: [
          { type: "text", id: "hub-title", text: "My Hub", variant: "pageTitle" },
          {
            type: "stats",
            id: "kpi-stats",
            stats: [
              { label: "Total Orders", value: "1,247", change: { direction: "up", value: "12%" }, trend: "positive" },
              { label: "Active Products", value: "86", change: { direction: "up", value: "3%" }, trend: "positive" },
              { label: "This Month Revenue", value: "₹2,84,500", change: { direction: "up", value: "18%" }, trend: "positive" },
              { label: "Pending KYC", value: "2", change: { direction: "down", value: "1" }, trend: "negative" },
            ],
          },
          {
            type: "section",
            id: "quick-actions",
            direction: "row",
            gap: 12,
            children: [
              { type: "card", id: "add-product-card", title: "Add Product", padding: 16, width: 200, children: [
                { type: "text", id: "add-product-desc", text: "List new items in your catalog", variant: "caption", color: "textSec" },
                { type: "actions", id: "add-product-btn", buttons: [{ label: "Add Product", variant: "primary" }] },
              ]},
              { type: "card", id: "view-orders-card", title: "Recent Orders", padding: 16, width: 200, children: [
                { type: "text", id: "view-orders-desc", text: "5 new orders today", variant: "caption", color: "textSec" },
                { type: "actions", id: "view-orders-btn", buttons: [{ label: "View Orders", variant: "outline" }] },
              ]},
            ],
          },
        ],
      },
    ],
  },

  "order-monitoring": {
    shell: "AdminAppShell",
    shellProps: { active: "Orders", pageTitle: "Order Monitoring" },
    regions: [
      {
        type: "section",
        id: "orders-page",
        direction: "column",
        padding: [14, 16],
        gap: 12,
        children: [
          { type: "text", id: "orders-title", text: "Order Monitoring", variant: "pageTitle" },
          {
            type: "card",
            id: "order-stats",
            padding: 12,
            children: [
              {
                type: "stats",
                id: "order-stat-cards",
                stats: [
                  { label: "Total", value: "247", change: { direction: "up", value: "8" }, trend: "positive" },
                  { label: "Pending", value: "23", change: { direction: "up", value: "3" }, trend: "neutral" },
                  { label: "Processing", value: "15", change: { direction: "down", value: "2" }, trend: "negative" },
                  { label: "Delivered Today", value: "31", change: { direction: "up", value: "5" }, trend: "positive" },
                ],
              },
            ],
          },
          {
            type: "card",
            id: "orders-table",
            title: "All Orders",
            titleAction: { label: "View All", action: "/orders" },
            padding: 0,
            children: [
              {
                type: "table",
                id: "order-rows",
                columns: [
                  { key: "orderId", label: "Order ID", width: 80 },
                  { key: "customer", label: "Customer", width: "1fr" },
                  { key: "items", label: "Items", width: 50 },
                  { key: "total", label: "Total", width: 70 },
                  { key: "status", label: "Status", width: 80 },
                ],
                items: [
                  { orderId: "#1042", customer: "Rahul Sharma", items: "3", total: "₹1,240", status: { badge: "Processing", type: "processing" } },
                  { orderId: "#1041", customer: "Priya Patel", items: "1", total: "₹450", status: { badge: "Delivered", type: "delivered" } },
                  { orderId: "#1040", customer: "Amit Kumar", items: "5", total: "₹2,830", status: { badge: "Placed", type: "placed" } },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  "discover-page": {
    shell: "CustAppShell",
    shellProps: { active: "Discover" },
    regions: [
      {
        type: "section",
        id: "discover-content",
        direction: "column",
        padding: [14, 14],
        gap: 12,
        children: [
          { type: "text", id: "discover-title", text: "Discover & Order", variant: "pageTitle" },
          { type: "text", id: "discover-subtitle", text: "Find what you need from hubs near you", variant: "body", color: "textSec" },
          {
            type: "card",
            id: "search-card",
            padding: 16,
            children: [
              {
                type: "form",
                id: "search-form",
                fields: [
                  { id: "search-input", type: "text", label: "Search Items or Business", placeholder: "Search..." },
                ],
                submit: { label: "Search", variant: "primary" },
              },
            ],
          },
        ],
      },
    ],
  },

  /* ─── Remaining ZiniHub auth screens ─── */
  "admin-register": {
    shell: "PublicAuthShell",
    shellProps: {},
    regions: [{ type: "section", id: "form-area", direction: "column", align: "center", justify: "center", children: [{ type: "card", id: "register-card", padding: 32, width: 400, children: [
      { type: "text", id: "register-title", text: "Create Your Hub", variant: "pageTitle", align: "center" },
      { type: "form", id: "register-form", fields: [
        { id: "mobile", type: "phone", label: "Mobile Number", required: true, countryCode: true },
      ], submit: { label: "SEND OTP", variant: "primary", width: "100%" } },
      { type: "text", id: "login-link", text: "Already registered? Sign in", variant: "caption", color: "brand", align: "center" },
    ]}]}]},
  "register-s2": {
    shell: "PublicAuthShell",
    shellProps: {},
    regions: [{ type: "section", id: "form-area", direction: "column", align: "center", justify: "center", children: [{ type: "card", id: "register-card", padding: 32, width: 400, children: [
      { type: "text", id: "register-title", text: "Set Up Your Account", variant: "pageTitle", align: "center" },
      { type: "form", id: "register-form-s2", fields: [
        { id: "full-name", type: "text", label: "Full Name", required: true },
        { id: "email", type: "email", label: "Email Address", required: true },
        { id: "password", type: "password", label: "Create Password", required: true },
      ], submit: { label: "CONTINUE", variant: "primary", width: "100%" } },
    ]}]}]},
  "register-s3": {
    shell: "PublicAuthShell",
    shellProps: {},
    regions: [{ type: "section", id: "form-area", direction: "column", align: "center", justify: "center", children: [{ type: "card", id: "register-card", padding: 32, width: 400, children: [
      { type: "text", id: "register-title", text: "Almost Done", variant: "pageTitle", align: "center" },
      { type: "form", id: "register-form-s3", fields: [
        { id: "hub-name", type: "text", label: "Hub Name", required: true, placeholder: "My Store" },
        { id: "hub-category", type: "select", label: "Category", required: true, options: [{label:"Food & Beverage",value:"food"},{label:"Retail",value:"retail"},{label:"Services",value:"services"}] },
      ], submit: { label: "CREATE HUB", variant: "primary", width: "100%" } },
    ]}]}]},
  "admin-reset": {
    shell: "PublicAuthShell",
    shellProps: {},
    regions: [{ type: "section", id: "form-area", direction: "column", align: "center", justify: "center", children: [{ type: "card", id: "reset-card", padding: 32, width: 400, children: [
      { type: "text", id: "reset-title", text: "Reset Password", variant: "pageTitle", align: "center" },
      { type: "text", id: "reset-subtitle", text: "Enter your mobile to receive an OTP", variant: "body", color: "textSec", align: "center" },
      { type: "form", id: "reset-form", fields: [
        { id: "mobile", type: "phone", label: "Mobile Number", required: true, countryCode: true },
      ], submit: { label: "SEND OTP", variant: "primary", width: "100%" } },
      { type: "text", id: "back-to-login", text: "Back to Login", variant: "caption", color: "brand", align: "center" },
    ]}]}]},
  "admin-reset-s2": {
    shell: "PublicAuthShell",
    shellProps: {},
    regions: [{ type: "section", id: "form-area", direction: "column", align: "center", justify: "center", children: [{ type: "card", id: "reset-card", padding: 32, width: 400, children: [
      { type: "text", id: "reset-title", text: "Set New Password", variant: "pageTitle", align: "center" },
      { type: "form", id: "reset-form-s2", fields: [
        { id: "otp", type: "otp", label: "Enter OTP", required: true },
        { id: "new-password", type: "password", label: "New Password", required: true },
        { id: "confirm-password", type: "password", label: "Confirm Password", required: true },
      ], submit: { label: "RESET PASSWORD", variant: "primary", width: "100%" } },
    ]}]}]},
  "cust-reset": {
    shell: "CustAuthShell",
    shellProps: {},
    regions: [{ type: "section", id: "form-area", direction: "column", align: "center", justify: "center", children: [{ type: "card", id: "reset-card", padding: 28, width: 380, children: [
      { type: "text", id: "reset-title", text: "Reset Password", variant: "pageTitle", align: "center" },
      { type: "form", id: "reset-form", fields: [
        { id: "mobile", type: "phone", label: "Mobile Number", required: true, countryCode: true },
      ], submit: { label: "SEND OTP", variant: "primary", width: "100%" } },
    ]}]}]},
  "cust-reset-s2": {
    shell: "CustAuthShell",
    shellProps: {},
    regions: [{ type: "section", id: "form-area", direction: "column", align: "center", justify: "center", children: [{ type: "card", id: "reset-card", padding: 28, width: 380, children: [
      { type: "text", id: "reset-title", text: "Set New Password", variant: "pageTitle", align: "center" },
      { type: "form", id: "reset-form-s2", fields: [
        { id: "otp", type: "otp", label: "Enter OTP", required: true },
        { id: "new-password", type: "password", label: "New Password", required: true },
      ], submit: { label: "RESET", variant: "primary", width: "100%" } },
    ]}]}]},

  /* ─── Admin dashboard screens ─── */
  "biz-grid": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "My Businesses" },
    regions: [{ type: "section", id: "biz-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "biz-title", text: "My Businesses", variant: "pageTitle" },
      { type: "section", id: "biz-grid", direction: "grid", gap: 12, grid: { template: "auto-fill", minColumnWidth: 200 }, children: [] },
      { type: "actions", id: "add-biz", buttons: [{ label: "Add Business", variant: "primary" }] },
    ]}]},
  "biz-metadata": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "New Business" },
    regions: [{ type: "section", id: "biz-form", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "form-title", text: "Business Details", variant: "pageTitle" },
      { type: "card", id: "details-card", padding: 20, children: [
        { type: "form", id: "biz-details-form", fields: [
          { id: "biz-name", type: "text", label: "Business Name", required: true },
          { id: "biz-type", type: "select", label: "Type", required: true, options: [{label:"Product",value:"product"},{label:"Service",value:"service"},{label:"Both",value:"both"}] },
          { id: "description", type: "textarea", label: "Description" },
          { id: "phone", type: "phone", label: "Contact Phone", required: true, countryCode: true },
        ], submit: { label: "SAVE & CONTINUE", variant: "primary", width: "100%" } },
      ]},
    ]}]},
  "biz-settings": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "Business Settings" },
    regions: [{ type: "section", id: "settings-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "settings-title", text: "Business Settings", variant: "pageTitle" },
      { type: "card", id: "settings-card", padding: 20, children: [
        { type: "form", id: "settings-form", fields: [
          { id: "biz-name", type: "text", label: "Business Name", required: true },
          { id: "biz-timings", type: "select", label: "Default Timings", options: [{label:"9 AM - 9 PM",value:"1"},{label:"10 AM - 10 PM",value:"2"}] },
          { id: "delivery-enabled", type: "checkbox group", label: "Delivery Options", options: [{label:"Home Delivery",value:"delivery"},{label:"Pickup",value:"pickup"}] },
        ], submit: { label: "SAVE CHANGES", variant: "primary", width: "100%" } },
      ]},
    ]}]},
  "item-catalog-empty": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "Item Catalog" },
    regions: [{ type: "section", id: "catalog-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "catalog-title", text: "Item Catalog", variant: "pageTitle" },
      { type: "card", id: "empty-card", padding: 40, children: [
        { type: "text", id: "empty-icon", text: "📦", variant: "hero", align: "center" },
        { type: "text", id: "empty-title", text: "No items yet", variant: "sectionTitle", align: "center" },
        { type: "text", id: "empty-desc", text: "Add your first product or service", variant: "body", color: "textSec", align: "center" },
        { type: "actions", id: "empty-actions", buttons: [{ label: "Add Item", variant: "primary" }], justify: "center" },
      ]},
    ]}]},
  "item-catalog": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "Item Catalog" },
    regions: [{ type: "section", id: "catalog-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "catalog-title", text: "Item Catalog", variant: "pageTitle" },
      { type: "actions", id: "catalog-actions", buttons: [{ label: "Add Item", variant: "primary" }] },
      { type: "card", id: "catalog-table", padding: 0, children: [
        { type: "table", id: "item-rows", columns: [
          { key: "name", label: "Item Name", width: "1fr" },
          { key: "price", label: "Price", width: 70 },
          { key: "category", label: "Category", width: 80 },
          { key: "status", label: "Status", width: 70 },
        ], items: [
          { name: "Classic Burger", price: "₹180", category: "Food", status: { badge: "Active", type: "active" } },
          { name: "Farmhouse Pizza", price: "₹350", category: "Food", status: { badge: "Active", type: "active" } },
          { name: "French Fries", price: "₹99", category: "Sides", status: { badge: "Draft", type: "draft" } },
        ]},
      ]},
    ]}]},
  "product-form": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "Add Item" },
    regions: [{ type: "section", id: "product-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "product-title", text: "Add Item", variant: "pageTitle" },
      { type: "card", id: "product-card", padding: 20, children: [
        { type: "form", id: "product-form", fields: [
          { id: "item-name", type: "text", label: "Item Name", required: true },
          { id: "description", type: "textarea", label: "Description" },
          { id: "category", type: "select", label: "Category", required: true, options: [{label:"Food",value:"food"},{label:"Beverage",value:"beverage"}] },
          { id: "price", type: "text", label: "Price (₹)", required: true },
          { id: "addons", type: "checkbox group", label: "Add-ons", options: [{label:"Extra Cheese +₹30",value:"cheese"},{label:"Large Size +₹50",value:"large"}] },
        ], submit: { label: "SAVE ITEM", variant: "primary", width: "100%" } },
      ]},
    ]}]},
  "product-builder": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "Product Builder" },
    regions: [{ type: "section", id: "builder-page", direction: "row", padding: [14, 16], gap: 16, children: [
      { type: "card", id: "builder-form", padding: 20, width: 320, children: [
        { type: "text", id: "builder-title", text: "Configure Variant", variant: "sectionTitle" },
        { type: "form", id: "variant-form", fields: [
          { id: "variant-name", type: "text", label: "Variant Name", required: true },
          { id: "variant-price", type: "text", label: "Price (₹)", required: true },
          { id: "variant-sku", type: "text", label: "SKU" },
        ], submit: { label: "ADD VARIANT", variant: "primary", width: "100%" } },
      ]},
      { type: "card", id: "builder-preview", padding: 20, children: [
        { type: "text", id: "preview-title", text: "Variants", variant: "sectionTitle" },
        { type: "table", id: "variant-list", columns: [
          { key: "name", label: "Name", width: "1fr" },
          { key: "price", label: "Price", width: 70 },
          { key: "sku", label: "SKU", width: 70 },
        ], items: [] },
        { type: "actions", id: "builder-actions", buttons: [{ label: "Save Product", variant: "primary" }] },
      ]},
    ]}]},
  "image-gallery": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "Image Gallery" },
    regions: [{ type: "section", id: "gallery-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "gallery-title", text: "Image Gallery", variant: "pageTitle" },
      { type: "actions", id: "gallery-actions", buttons: [{ label: "Upload Images", variant: "primary" }, { label: "Organize", variant: "outline" }] },
      { type: "section", id: "gallery-grid", direction: "grid", gap: 12, grid: { template: "auto-fill", minColumnWidth: 150 }, children: [] },
    ]}]},
  "hub-settings-main": {
    shell: "AdminAppShell",
    shellProps: { active: "My Hub", pageTitle: "Hub Settings" },
    regions: [{ type: "section", id: "settings-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "settings-title", text: "Hub Settings", variant: "pageTitle" },
      { type: "card", id: "settings-card", padding: 20, children: [
        { type: "form", id: "settings-form", fields: [
          { id: "hub-name", type: "text", label: "Hub Name", required: true },
          { id: "address", type: "textarea", label: "Address", required: true },
          { id: "timezone", type: "select", label: "Timezone", options: [{label:"IST (UTC+5:30)",value:"ist"},{label:"GST (UTC+4)",value:"gst"}] },
        ], submit: { label: "SAVE", variant: "primary", width: "100%" } },
      ]},
    ]}]},
  "sub-overview": {
    shell: "AdminAppShell",
    shellProps: { active: "My Hub", pageTitle: "Subscription" },
    regions: [{ type: "section", id: "sub-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "sub-title", text: "Subscription", variant: "pageTitle" },
      { type: "card", id: "current-plan", padding: 20, children: [
        { type: "text", id: "plan-name", text: "Professional Plan", variant: "sectionTitle" },
        { type: "text", id: "plan-desc", text: "₹1,499/mo — Renews on June 15, 2026", variant: "body", color: "textSec" },
        { type: "stats", id: "plan-stats", stats: [
          { label: "Products", value: "86 / 100" },
          { label: "Staff", value: "3 / 5" },
          { label: "Storage", value: "2.1 GB / 5 GB" },
        ]},
        { type: "actions", id: "sub-actions", buttons: [{ label: "Upgrade Plan", variant: "primary" }, { label: "Cancel", variant: "ghost" }] },
      ]},
    ]}]},
  "biz-preview": {
    shell: "AdminAppShell",
    shellProps: { active: "Dashboard", pageTitle: "Business Preview" },
    regions: [{ type: "section", id: "preview-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "preview-title", text: "Customer Preview", variant: "pageTitle" },
      { type: "card", id: "preview-card", padding: 20, width: 600, children: [
        { type: "tabs", id: "preview-tabs", tabs: [{label:"Products",id:"products"},{label:"Services",id:"services"}], activeTab: "products" },
        { type: "text", id: "preview-note", text: "This is how customers see your business", variant: "caption", color: "textSec" },
      ]},
    ]}]},

  /* ─── KYC screens ─── */
  "kyc-pending": {
    shell: "AdminAppShell",
    shellProps: { active: "My Hub", pageTitle: "KYC" },
    regions: [{ type: "section", id: "kyc-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "kyc-title", text: "KYC Verification", variant: "pageTitle" },
      { type: "card", id: "kyc-status-card", padding: 20, children: [
        { type: "text", id: "kyc-notice", text: "Complete your KYC to activate your hub", variant: "sectionTitle" },
        { type: "actions", id: "kyc-start", buttons: [{ label: "Start KYC", variant: "primary", width: "100%" }] },
      ]},
    ]}]},
  "kyc-requested": {
    shell: "AdminAppShell",
    shellProps: { active: "My Hub", pageTitle: "KYC" },
    regions: [{ type: "section", id: "kyc-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "kyc-title", text: "KYC Verification", variant: "pageTitle" },
      { type: "card", id: "kyc-status-card", padding: 20, children: [
        { type: "text", id: "kyc-status", text: "Under Review", variant: "sectionTitle", color: "brand" },
        { type: "progress", id: "kyc-progress", steps: [{label:"Submitted",state:"completed"},{label:"Verification",state:"current"},{label:"Approved",state:"pending"}], activeStep: 1 },
        { type: "text", id: "kyc-eta", text: "Estimated time: 24-48 hours", variant: "caption", color: "textSec" },
      ]},
    ]}]},
  "kyc-verified": {
    shell: "AdminAppShell",
    shellProps: { active: "My Hub", pageTitle: "KYC" },
    regions: [{ type: "section", id: "kyc-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "kyc-title", text: "KYC Verification", variant: "pageTitle" },
      { type: "card", id: "kyc-status-card", padding: 20, children: [
        { type: "text", id: "kyc-status", text: "✓ Verified", variant: "sectionTitle", color: "text" },
        { type: "text", id: "kyc-message", text: "Your KYC has been approved", variant: "body", color: "textSec" },
      ]},
    ]}]},
  "kyc-rejected": {
    shell: "AdminAppShell",
    shellProps: { active: "My Hub", pageTitle: "KYC" },
    regions: [{ type: "section", id: "kyc-page", direction: "column", padding: [14, 16], gap: 12, children: [
      { type: "text", id: "kyc-title", text: "KYC Verification", variant: "pageTitle" },
      { type: "card", id: "kyc-status-card", padding: 20, children: [
        { type: "text", id: "kyc-status", text: "Rejected", variant: "sectionTitle", color: "danger" },
        { type: "text", id: "kyc-message", text: "Documents did not meet requirements. Please re-upload.", variant: "body", color: "textSec" },
        { type: "actions", id: "kyc-retry", buttons: [{ label: "Re-upload Documents", variant: "primary", width: "100%" }] },
      ]},
    ]}]},

  /* ─── Customer app screens ─── */
  "hub-storefront": {
    shell: "CustAppShell",
    shellProps: { active: "Home", showBizOrders: true, cartCount: 2 },
    regions: [{ type: "section", id: "storefront-page", direction: "column", padding: [12, 12], gap: 8, children: [
      { type: "text", id: "storefront-title", text: "My Hub Storefront", variant: "pageTitle" },
      { type: "section", id: "biz-card-grid", direction: "grid", gap: 12, grid: { template: "auto-fill", minColumnWidth: 140 }, children: [] },
    ]}]},
  "biz-page": {
    shell: "CustAppShell",
    shellProps: { loggedIn: true },
    regions: [{ type: "section", id: "biz-detail", direction: "column", padding: [12, 14], gap: 12, children: [
      { type: "tabs", id: "biz-tabs", tabs: [{label:"Products",id:"products"},{label:"Services",id:"services"}], activeTab: "products" },
      { type: "section", id: "product-grid", direction: "grid", gap: 12, grid: { template: "auto-fill", minColumnWidth: 140 }, children: [] },
    ]}]},
  "biz-private": {
    shell: "CustAppShell",
    shellProps: { loggedIn: true },
    regions: [{ type: "section", id: "biz-detail", direction: "column", align: "center", justify: "center", children: [
      { type: "card", id: "passkey-card", padding: 28, width: 340, children: [
        { type: "text", id: "passkey-title", text: "This Business is Private", variant: "sectionTitle", align: "center" },
        { type: "text", id: "passkey-desc", text: "Enter passkey to view", variant: "body", color: "textSec", align: "center" },
        { type: "form", id: "passkey-form", fields: [
          { id: "passkey", type: "text", label: "Passkey", required: true },
        ], submit: { label: "UNLOCK", variant: "primary", width: "100%" } },
      ]},
    ]}]},
  "cart-filled": {
    shell: "CustAppShell",
    shellProps: { loggedIn: true, cartCount: 3 },
    regions: [{ type: "section", id: "cart-layout", direction: "row", children: [
      { type: "section", id: "cart-backdrop", direction: "column", children: [] },
      { type: "overlay", id: "cart-drawer", variant: "drawer-right", width: 280, children: [
        { type: "text", id: "cart-header", text: "My Cart (3)", variant: "sectionTitle", padding: 16 },
        { type: "list", id: "cart-items", items: [
          { label: "Classic Burger ×2", value: "₹360" },
          { label: "Farmhouse Pizza ×1", value: "₹350" },
          { label: "French Fries ×1", value: "₹99" },
        ]},
        { type: "text", id: "cart-total", text: "Total: ₹809", variant: "cardTitle", padding: 16 },
        { type: "actions", id: "cart-checkout", buttons: [{ label: "CHECKOUT", variant: "primary", width: "100%" }] },
      ]},
    ]}]},
  "checkout-page": {
    shell: "CustAppShell",
    shellProps: { loggedIn: true },
    regions: [{ type: "section", id: "checkout-page", direction: "column", padding: [12, 14], gap: 12, scrollable: true, children: [
      { type: "text", id: "checkout-title", text: "Checkout", variant: "pageTitle" },
      { type: "card", id: "delivery-card", title: "Delivery Details", children: [
        { type: "key-value", id: "delivery-info", items: [
          { label: "Name", value: "Rahul Sharma" },
          { label: "Contact", value: "+91-9876543210" },
          { label: "Email", value: "rahul@email.com" },
        ]},
      ]},
      { type: "card", id: "order-summary-card", title: "Order Summary", children: [
        { type: "list", id: "order-items", items: [
          { label: "Classic Burger ×2", value: "₹360" },
          { label: "Farmhouse Pizza ×1", value: "₹350" },
        ]},
      ]},
      { type: "actions", id: "place-order", buttons: [{ label: "Place Order", variant: "primary", width: "100%" }] },
    ]}]},
  "orders-unified": {
    shell: "CustAppShell",
    shellProps: { active: "Orders", loggedIn: true },
    regions: [{ type: "section", id: "orders-page", direction: "column", padding: [12, 14], gap: 12, children: [
      { type: "text", id: "orders-title", text: "My Orders", variant: "pageTitle" },
      { type: "tabs", id: "orders-tabs", tabs: [{label:"Active",id:"active"},{label:"Past",id:"past"}], activeTab: "active" },
      { type: "table", id: "orders-table", columns: [
        { key: "orderId", label: "Order", width: 70 },
        { key: "business", label: "Business", width: "1fr" },
        { key: "items", label: "Items", width: 50 },
        { key: "total", label: "Total", width: 60 },
        { key: "status", label: "Status", width: 70 },
      ], items: [
        { orderId: "#1042", business: "Pizza Hub", items: "3", total: "₹1,240", status: { badge: "Preparing", type: "processing" } },
        { orderId: "#1041", business: "Burger Point", items: "2", total: "₹450", status: { badge: "Delivered", type: "delivered" } },
      ]},
    ]}]},
  "profile-view": {
    shell: "CustAppShell",
    shellProps: { active: "Profile", loggedIn: true },
    regions: [{ type: "section", id: "profile-page", direction: "column", padding: [12, 14], gap: 12, children: [
      { type: "text", id: "profile-title", text: "My Profile", variant: "pageTitle" },
      { type: "card", id: "personal-card", title: "Personal Details", titleAction: { label: "Edit", action: "/profile/edit" }, children: [
        { type: "key-value", id: "personal-info", items: [
          { label: "Name", value: "Rahul Sharma" },
          { label: "Email", value: "rahul@email.com" },
          { label: "Phone", value: "+91-9876543210" },
        ]},
      ]},
      { type: "card", id: "address-card", title: "Saved Addresses", titleAction: { label: "Edit", action: "/profile/addresses" }, children: [
        { type: "list", id: "address-list", items: [{ label: "Home", value: "123, Main Street, Bangalore" }] },
      ]},
    ]}]},

  /* ─── Auth outcome variants (46 screens from template) ─── */
  /* These follow a uniform pattern: shell + card with alert + optional OTP box.
     The SCREEN_CATALOG entries define their states. The doit agent should
      generate each variant from the base screen layout schema, overlaying
     the state-specific alert bar and OTP controls. */
  AUTH_OUTCOME_VARIANTS: {
    template: {
      type: "section",
      id: "auth-outcome",
      direction: "column",
      align: "center",
      justify: "center",
      children: [
        {
          type: "card",
          id: "outcome-card",
          padding: 32,
          width: 400,
          children: [
            { type: "text", id: "outcome-alert", text: "{{message}}", variant: "sectionTitle", color: "{{alertColor}}" },
            { type: "form", id: "otp-form", optional: true, fields: [
              { id: "otp", type: "otp", label: "Enter OTP", required: true },
            ], submit: { label: "VERIFY", variant: "primary", width: "100%" } },
            { type: "actions", id: "outcome-actions", buttons: [
              { label: "Back", variant: "outline" },
              { label: "Continue", variant: "primary" },
            ], justify: "between" },
          ],
        },
      ],
    },
    /* Screens that use this template; their base screens define shell/auth context */
    screens: [
      "admin-login-success", "admin-login-invalid-phone", "admin-login-country-code-mismatch",
      "admin-login-wrong-password", "admin-login-locked",
      "admin-register-otp-cooldown", "admin-register-success-redirect", "admin-register-invalid-phone",
      "admin-register-country-code-mismatch", "admin-register-duplicate-account",
      "admin-register-resend-enabled", "admin-register-wrong-otp", "admin-register-otp-expired",
      "admin-register-max-attempts-lockout",
      "admin-reset-otp-cooldown", "admin-reset-success", "admin-reset-failure",
      "admin-reset-invalid-phone", "admin-reset-country-code-mismatch", "admin-reset-resend-enabled",
      "admin-reset-wrong-otp", "admin-reset-otp-expired", "admin-reset-max-attempts-lockout",
      "cust-login-success", "cust-login-invalid-phone", "cust-login-country-code-mismatch",
      "cust-login-wrong-password", "cust-login-locked",
      "cust-register-otp-cooldown", "cust-register-success-redirect", "cust-register-invalid-phone",
      "cust-register-country-code-mismatch", "cust-register-duplicate-account",
      "cust-register-resend-enabled", "cust-register-wrong-otp", "cust-register-otp-expired",
      "cust-register-max-attempts-lockout",
      "cust-reset-otp-cooldown", "cust-reset-success", "cust-reset-failure",
      "cust-reset-invalid-phone", "cust-reset-country-code-mismatch", "cust-reset-resend-enabled",
      "cust-reset-wrong-otp", "cust-reset-otp-expired", "cust-reset-max-attempts-lockout",
    ],
  },
};
